import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Snackbar, Button } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { setBlocking, clearBlocking } from '../../../irmlib/src/containers'
import { ScreenCover } from '../../../irmlib/src/styles'
import { toastZIndex } from '../../../irmlib/src/common'

const severityAccentMap = {
  error: '#d32f2f',
  success: '#2e7d32',
  warning: '#ed6c02',
  info: '#0288d1',
}

const useStyles = makeStyles(() => ({
  alert: {
    minWidth: '340px',
    alignItems: 'center',
  },
  snackbar: {
    zIndex: toastZIndex + 2,
  },
}))

const CustomActionSnackBar = ({
  open,
  onClose,
  message,
  severity,
  actions,
  isBlocking = false,
  autoHideDuration = null,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
}) => {
  const classes = useStyles()
  const accentColor = severityAccentMap[severity] || severityAccentMap.info
  const blockingActiveRef = useRef(false)

  // Manage the global blocking counter when isBlocking + open changes
  useEffect(() => {
    if (open && isBlocking && !blockingActiveRef.current) {
      setBlocking('CustomActionSnackBar')
      blockingActiveRef.current = true
    } else if ((!open || !isBlocking) && blockingActiveRef.current) {
      clearBlocking('CustomActionSnackBar')
      blockingActiveRef.current = false
    }

    // Cleanup on unmount -- always clear if we were blocking
    return () => {
      if (blockingActiveRef.current) {
        clearBlocking('CustomActionSnackBar')
        blockingActiveRef.current = false
      }
    }
  }, [open, isBlocking])

  // When blocking, ignore clickaway so user must use action buttons or close icon
  const handleClose = (event, reason) => {
    if (isBlocking && reason === 'clickaway') {
      return
    }
    onClose(event, reason)
  }

  const actionButtons = (
    <>
      {actions?.secondary?.label && actions?.secondary?.handler && (
        <Button
          size="small"
          onClick={actions.secondary.handler}
          style={{ color: accentColor, textTransform: 'none' }}
        >
          {actions.secondary.label}
        </Button>
      )}
      {actions?.primary?.label && actions?.primary?.handler && (
        <Button
          variant="outlined"
          size="small"
          onClick={actions.primary.handler}
          style={{ color: accentColor, borderColor: accentColor, textTransform: 'none', fontWeight: 600 }}
        >
          {actions.primary.label}
        </Button>
      )}
    </>
  )

  const snackbar = (
    <Snackbar
      className={isBlocking ? classes.snackbar : undefined}
      open={open}
      autoHideDuration={isBlocking ? null : autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
    >
      <Alert
        className={classes.alert}
        onClose={handleClose}
        severity={severity}
        variant="standard"
        action={actionButtons}
        elevation={3}
      >
        {message}
      </Alert>
    </Snackbar>
  )

  // When blocking, wrap with a ScreenCover to prevent background interaction
  if (open && isBlocking) {
    return (
      <ScreenCover zIndex={toastZIndex + 1}>
        {snackbar}
      </ScreenCover>
    )
  }

  return snackbar
}

CustomActionSnackBar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  actions: PropTypes.shape({
    primary: PropTypes.shape({
      label: PropTypes.string,
      handler: PropTypes.func,
    }),
    secondary: PropTypes.shape({
      label: PropTypes.string,
      handler: PropTypes.func,
    }),
  }),
  isBlocking: PropTypes.bool,
  autoHideDuration: PropTypes.number,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
}

export default CustomActionSnackBar
